import { useState, useCallback, useRef, useEffect } from "react";
import { api } from "../api";
import { Document } from "../../types/documents";
import { ShowToast } from "../../components/toast";

const ALLOWED_EXTENSIONS = [".md", ".markdown"];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

type ValidationResult = { ok: true } | { ok: false; reason: string };

function validateMarkdownFile(file: File): ValidationResult {
  const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    return {
      ok: false,
      reason: `${file.name}: only Markdown (.md) files are supported.`,
    };
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return {
      ok: false,
      reason: `${file.name}: file size must be less than 10MB.`,
    };
  }
  return { ok: true };
}

export type PendingUpload = { tempId: string; name: string };

let tempIdCounter = 0;
function nextTempId() {
  tempIdCounter += 1;
  return `pending-${Date.now()}-${tempIdCounter}`;
}

export function useDocuments() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const documentsRef = useRef<Document[]>([]);
  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  const fetchDocuments = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.documents.list();
      setDocuments(data);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || "Failed to load documents";
      setError(errorMessage);
      ShowToast({ message: errorMessage, type: "error" });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const regenerateDocument = useCallback(async (documentId: string) => {
    try {
      const response = await api.documents.regenerate(documentId);
      ShowToast({
        message: response.message || "Embeddings regenerated successfully",
        type: "success",
      });
      await fetchDocuments();
      return response;
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || "Failed to regenerate embeddings";
      ShowToast({ message: errorMessage, type: "error" });
      throw err;
    }
  }, [fetchDocuments]);

  const downloadDocument = useCallback(async (documentId: string) => {
    try {
      const { url } = await api.documents.download(documentId);
      window.open(url, "_blank");
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || "Failed to download document";
      ShowToast({ message: errorMessage, type: "error" });
      throw err;
    }
  }, []);

  const deleteDocument = useCallback(async (documentId: string) => {
    const current = documentsRef.current;
    const index = current.findIndex((d) => d.id === documentId);
    if (index === -1) return;
    const doc = current[index];

    setDocuments((prev) => prev.filter((d) => d.id !== documentId));

    try {
      await api.documents.delete(documentId);
      ShowToast({ message: "Document deleted.", type: "success" });
    } catch (err: any) {
      setDocuments((prev) => [
        ...prev.slice(0, index),
        doc,
        ...prev.slice(index),
      ]);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to delete document";
      ShowToast({ message: msg, type: "error" });
      throw err;
    }
  }, []);

  const uploadDocument = useCallback(async (data: {
    userId: string;
    name: string;
    url: string;
    mimeType: string;
    ownerType: 'S';
  }) => {
    try {
      const newDocument = await api.documents.upload(data);
      ShowToast({
        message: "Document uploaded successfully. Generating embeddings...",
        type: "success",
      });
      await fetchDocuments();
      return newDocument;
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || "Failed to upload document";
      ShowToast({ message: errorMessage, type: "error" });
      throw err;
    }
  }, [fetchDocuments]);

  const updateDocument = useCallback(
    async (
      documentId: string,
      data: { content?: string; name?: string }
    ) => {
      try {
        const updated = await api.documents.update(documentId, data);
        ShowToast({
          message:
            data.content !== undefined
              ? "Document saved. Regenerating embeddings…"
              : "Document renamed.",
          type: "success",
        });
        await fetchDocuments();
        return updated as Document;
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to save document";
        ShowToast({ message: msg, type: "error" });
        throw err;
      }
    },
    [fetchDocuments]
  );

  const uploadOneFile = useCallback(
    async (userId: string, file: File, tempId: string) => {
      try {
        const { uploadUrl, accessUrl } = await api.getSignedUrl(userId, {
          uploadType: "DOCUMENT",
          mimeType: file.type || "application/octet-stream",
          fileName: file.name,
        });

        const putResponse = await fetch(uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type || "application/octet-stream",
          },
        });
        if (!putResponse.ok) {
          throw new Error(
            `S3 upload failed: ${putResponse.status} ${putResponse.statusText}`
          );
        }

        const created = (await api.documents.upload({
          userId,
          name: file.name,
          url: accessUrl,
          mimeType: file.type || "application/octet-stream",
          ownerType: "S",
        })) as Document;

        setDocuments((prev) => [created, ...prev]);
        ShowToast({
          message: `${file.name} uploaded. Generating embeddings…`,
          type: "success",
        });
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          `Failed to upload ${file.name}`;
        ShowToast({ message: `${file.name}: ${msg}`, type: "error" });
      } finally {
        setPendingUploads((prev) => prev.filter((p) => p.tempId !== tempId));
      }
    },
    []
  );

  const uploadFiles = useCallback(
    (userId: string, files: File[]): void => {
      if (files.length === 0) return;

      const queued: { file: File; tempId: string }[] = [];
      for (const file of files) {
        const check = validateMarkdownFile(file);
        if (check.ok) {
          queued.push({ file, tempId: nextTempId() });
        } else {
          ShowToast({ message: check.reason, type: "error" });
        }
      }
      if (queued.length === 0) return;

      setPendingUploads((prev) => [
        ...prev,
        ...queued.map((q) => ({ tempId: q.tempId, name: q.file.name })),
      ]);

      (async () => {
        for (const { file, tempId } of queued) {
          await uploadOneFile(userId, file, tempId);
        }
      })();
    },
    [uploadOneFile]
  );

  return {
    documents,
    isLoading,
    error,
    fetchDocuments,
    regenerateDocument,
    downloadDocument,
    deleteDocument,
    uploadDocument,
    uploadFiles,
    pendingUploads,
    updateDocument,
  };
}
